import type { Resource } from '@medplum/fhirtypes';
import type { ReactNode } from 'react';

import { Caption, Text } from '@eluve/components';

import { useRenderContext } from '../core/render-context';
import {
  type FieldVisibility,
  isResolvedFieldVisible,
  resolvePrimitiveField,
} from '../resolvers/field-state';
import {
  evaluateFieldPath,
  fieldPathString,
  schemaElementAt,
} from '../schema/fhir-schema';
import type {
  FhirFieldPath,
  FhirFieldPathValue,
  FieldPathSegments,
} from '../schema/field-path';

import { FieldStateMarker } from './FieldStateMarker';
import { datatypeDisplay } from './displays';

/**
 * The safety primitive for curated views: one typed path drives extraction,
 * policy identity (AccessPolicy hiddenFields), the data-absent-reason
 * sibling, AND datatype-appropriate display — so what is shown and what is
 * checked cannot drift, and an absent value renders its honest state
 * (not-recorded / DAR tooltip / withheld) instead of a bare em-dash.
 *
 * Paths are compile-time checked against @medplum/fhirtypes (see
 * field-path.ts): a typo'd element, wrong filter key, or navigation past a
 * primitive fails type-check. Never interpolate `data.*` raw in a fact
 * cell — that bypasses withheld semantics; use this.
 */
export function Field<T extends Resource, const P extends FhirFieldPath<T>>({
  label,
  data,
  path,
  render,
  visibility = 'always',
}: {
  label: string;
  data: T;
  /** Typed tuple path, e.g. ['telecom', { system: 'phone' }, 'value']. */
  path: P;
  /** Display override for computed presentation; state handling stays here. */
  render?: (value: FhirFieldPathValue<T, P>) => ReactNode;
  /** 'always' keeps the row with a state marker when absent (fact grids). */
  visibility?: FieldVisibility;
}) {
  const ctx = useRenderContext();
  const segments = path as FieldPathSegments;

  const element = schemaElementAt(data.resourceType, segments);
  if (!element) {
    // The type layer should make this unreachable; a forced cast or manifest
    // drift lands here. Dev-visible, never a crash, never a false value.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[fhir-renderer] <Field> path not in R4 schema: ${data.resourceType}.${fieldPathString(segments)}`,
      );
    }
  }

  const { value, owner } = evaluateFieldPath(data, segments);
  const dotted = fieldPathString(segments);
  // hiddenFields may name an ancestor element ('telecom' hides telecom.value):
  // every prefix of the path counts, bare and resource-qualified.
  const parts = dotted.split('.');
  const ancestorCandidates = parts.slice(0, -1).flatMap((_, index) => {
    const prefix = parts.slice(0, index + 1).join('.');
    return [prefix, `${data.resourceType}.${prefix}`];
  });
  const field = resolvePrimitiveField({
    resource: data,
    path: dotted,
    value,
    access: ctx.access,
    owner,
    policyCandidates: ancestorCandidates,
  });

  if (!isResolvedFieldVisible(field, visibility, 'read')) return null;

  return (
    <div className="min-w-0" data-fhir-field={fieldPathString(segments)}>
      <Caption color="tertiary">{label}</Caption>
      {'value' in field ? (
        <Text size="sm" weight="regular" className="truncate">
          {render
            ? render(field.value as FhirFieldPathValue<T, P>)
            : datatypeDisplay(element?.datatype ?? 'string', field.value)}
        </Text>
      ) : (
        <Text size="sm" weight="regular">
          <FieldStateMarker state={field.state} />
        </Text>
      )}
    </div>
  );
}
